<script lang="ts" setup>
    import '../../../css/choice.css';
    import '../../../css/patientFlag.css';
    import { onMounted, ref, reactive, computed, nextTick } from 'vue';
    import { initPinia } from '../../lib/PiniaMgr';
    import { usePrivilegesStore } from '../../lib/Stores/PrivilegesStore' 
    import ModalDialog from '../../components/ModalDialog.vue'
    import { ICol, IRowSelection, calculateGridRowCount, ValueStrDelegate } from "../../lib/ChoiceTableHelpers";
    import { ButtonDisplaySize, ModalSize, TableRowHighlight, TableRowType, ButtonDisplayStyle } from "../../Enums/UiEnums";
    import { ICheckInOutDashboardListItem, CheckInOutDashboardTab, ICheckInOutDashboardFilterItem, ICheckInOutDashboardAppointmentStatusItem, 
             ICheckInOutDashboardSelectedRow, AppointmentStatus, IPatientMultipleCheckInResults } from '../../lib/Models/ScheduleModel';
    import { NoTzDate} from "../../lib/DateUtils";
    import { IToolbar, ToolbarStyle, ToolbarSize, ToolbarBackground, ToolbarPadding } from "../../lib/Toolbar"
    import { ModalStyles, popAlert } from "../../lib/Notification";
    import { ISearchPatientItem } from '../../lib/Models/SearchModel';
    import { ActionButtonStyle } from "../../lib/Types/Types";
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import ResizeMonitor from '../../components/ResizeMonitor.vue'
    import DatePickerNoTZ from '../../components/DatePickerNoTZ.vue';
    import Combo from "../../components/Combo.vue";
    import TextInput from '../../components/TextInput.vue';
    import Checkbox from '../../components/Inputs/Checkbox.vue';
    import Toolbar from "../../components/Toolbar.vue"
    import PatientMultipleCheckIn from '../../ChoiceScreens/Screens/PatientMultipleCheckIn.vue';
    import CheckInOutFilter from '../../components/Schedule/CheckInOutFilter.vue';
    import CheckInOutStatusSetup from '../../components/Schedule/CheckInOutStatusSetup.vue';
    import CancelCommentLegacy from '../../ChoiceScreens/Screens/CancelCommentLegacy.vue';

    import ActionButtonV2 from '../../components/ActionButtonV2.vue'
    import * as ChoiceApi from '../../lib/ChoiceApi';

    const tableHeight = ref<number>();
    const privilegesStore = usePrivilegesStore(initPinia());

    const globalSearchButton = ref<InstanceType<typeof ActionButtonV2>>();

    const emit = defineEmits<{
        (e: "onChange", selectedItem: ICheckInOutDashboardSelectedRow): void, 
        (e: "onStatusChange", selectedItem: ICheckInOutDashboardSelectedRow): void
    }>();
    
    defineExpose({refreshNow});

    const props = defineProps<{
        tabId: CheckInOutDashboardTab,
        scheduleDate: string,
        multipleCheckIn: boolean,
        rescheduleCopy: boolean
    }>();

    const formatFilterName: ValueStrDelegate = (value: any, col: ICol, row: Record<string, any> | undefined, rowType: TableRowType): string | undefined => {
        if (rowType === TableRowType.Body)
        {
            const age = (row?.patientBirthDate  ?? "") != "" ? `${(row?.patientBirthDate ?? "")} (${(row?.ageText ?? "")}) ` : " ";
            return `${row?.patientName ?? ""} ${age}${row?.gender ?? ""}`;
        }
    }

    const formatPatientName: ValueStrDelegate = (value: any, col: ICol, row: Record<string, any> | undefined, rowType: TableRowType): string | undefined => {
        if (rowType === TableRowType.Body)
        {
            return `${row?.patient_name ?? ""} ${row?.birth_date ?? ""} (${row?.age ?? ""}) ${row?.gender ?? ""}`;
        }
    }

    const formData = reactive<{
        tableData: ICheckInOutDashboardListItem[],
        filterTableData: ICheckInOutDashboardListItem[],
        filterList: ICheckInOutDashboardFilterItem[],
        statusList: ICheckInOutDashboardAppointmentStatusItem[],
        scheduleDate?: NoTzDate,
        filterId?: number,
        defaultFilterId?: number,
        filterPatient?: string,
        searchPatient?: string,
        globalPatientSearch?: boolean,
        toolbarExecute: boolean,
        showFilter: boolean,
        keyFilter: number,
        keyStatus: number,
        searchTableData: ISearchPatientItem[],
        currentSelectedRow?: IRowSelection & ICheckInOutDashboardSelectedRow,
        currentSelectedRowTypeGlobalSearch: boolean,
        showStatus: boolean,
        displayMultipleCheckIn: boolean
        multipleCheckInResults: IPatientMultipleCheckInResults,
        showCancelComment: boolean,
        rescheduledAppointmentStatusId?: number,
        newAppointmentStatusId?: number
    }>({
        tableData: [],
        filterTableData: [],
        filterList: [],
        statusList: [],
        toolbarExecute: false,
        scheduleDate: props.scheduleDate,
        showFilter: false,
        keyFilter: 0, 
        keyStatus: 0,
        searchTableData: [],
        showStatus: false,
        currentSelectedRowTypeGlobalSearch: false,
        displayMultipleCheckIn: false,
        multipleCheckInResults: { appointmentList: []},
        showCancelComment: false
    });

    enum actionToolbar {
        appointmentStatus,
        refresh
    }

    const toolbarConfig = reactive<IToolbar>({
        toolbarList: [
        ]
    });

    const settingConfig = reactive<IToolbar>({
        toolbarList: [
            {identifier: "", label: "", tooltip:"Open the filter settings.", action: ActionButtonStyle.Setting, performAction: () => onShowFilter()},
        ]
    });    

    const filterColumns = ref<ICol[]>([
        { Name: 'Chart', DataField: 'patientId' },
        { Name: 'Patient Name', DataField: 'last_name', FormatValue: formatFilterName },
        { Name: 'Appt Type', DataField: 'appointmentType' },
        { Name: 'Resource Name', DataField: 'scheduledEntityName' },
        { Name: 'Location', DataField: 'serviceLocationName' },
        { Name: 'Status', DataField: 'appointmentStatus' },
        { Name: 'Time', DataField: 'startTime' },
    ]);

    const searchColumns = ref<ICol[]>([
        { Name: 'Chart', DataField: 'patient_id' },
        { Name: 'Patient Name', DataField: 'patient_name', FormatValue: formatPatientName },
        { Name: 'Appt Type', DataField: 'appointment_type_name' },
        { Name: 'Resource Name', DataField: 'resource_name' },
        { Name: 'Location', DataField: 'service_location_name' },
        { Name: 'Status', DataField: 'appointment_status_desc' },
        { Name: 'Time', DataField: 'next_appointment_display' },
    ]);

    async function fetchData(){
        const promiseFilterList = refreshFilterList();
        const promiseStatusList = refreshStatusList();
        await Promise.all([promiseFilterList, promiseStatusList]);
        await refreshTabData();
        await refreshSearch();
    }
    fetchData();

    onMounted(async () => {
        MessageLib.Scheduler.sink.cancelComment(cancelCommentSink);
        windowResize();
    });

    const tabTitle = computed<string>(() => props.tabId == CheckInOutDashboardTab.CheckIn ? "Check In" : "Check Out");
    const maxRowsReturned = computed<boolean>(() => formData.searchTableData.length >= 50);
    const multipleCheckInTitle = computed<string>(() => `Additional Check-In(s): ${formData.currentSelectedRow?.patientName}`);

    function windowResize() {
        tableHeight.value = window.innerHeight - 200;
    }

    async function refreshFilterList() {
        const result = await ChoiceApi.schedule.DashboardFilterList(props.tabId) ?? [];
        formData.filterList = result.filter(item => item.active);
        let defaultRow = formData.filterList.find(item => item.defaultFilter);
        if (defaultRow)
            defaultRow.description += " (Default)";
    }

    async function refreshStatusList() {
        formData.statusList = await ChoiceApi.schedule.DashboardStatusList(props.tabId) ?? [];
        const statusList = formData.statusList.filter(o => o.workflowEnabled);
        const menuList = statusList.map(o => ({identifier: o.fromStatusId, label: o.fromStatusDescription}));
        const defaultStatusId = statusList.length == 0 ? "-1" : statusList[0].fromStatusId;
        const defaultStatusDesc = statusList.length == 0 ? "" : statusList[0].fromStatusDescription;
        toolbarConfig.toolbarList = [
            {identifier: defaultStatusId, label: defaultStatusDesc, menuList: menuList},
            {identifier: "0", label: "", tooltip: "Refresh with the current settings.", action: ActionButtonStyle.Refresh, performAction: () => refreshNow()},
            {identifier: "1", label: "", tooltip: "Open the automated workflow settings.", action: ActionButtonStyle.Setting, performAction: () => onShowStatus()},
        ]
    }

    async function refreshTabData() {
        const tabData = await ChoiceApi.schedule.DashboardTab(props.tabId);
        formData.defaultFilterId = tabData.defaultFilterId;
        formData.filterId = formData.defaultFilterId;
        formData.rescheduledAppointmentStatusId = tabData.rescheduledAppointmentStatusId;
    }

    async function refreshSearch() {
        if (formData.scheduleDate==undefined)
            return;
        const post_data = {
            scheduleDate: formData.scheduleDate,
            tabId: props.tabId,
            filterId: formData.filterId
        }
        formData.tableData = await ChoiceApi.schedule.DashboardSearch(post_data) ?? [];
        setFilterTableData();
    }

    async function refreshNow() {
        if (formData.globalPatientSearch)
            await onPatientSearch();
        else
            await refreshSearch();
    }


    function setFilterTableData() {
        if (formData.filterPatient == undefined) {
            formData.filterTableData = [...formData.tableData];
            return;
        }

        formData.filterTableData = formData.tableData.filter(item => item.patientId.toString().indexOf(formData.filterPatient ?? "") >= 0 ||
            (item.patientName ?? "").toLowerCase().indexOf(formData.filterPatient?.toLowerCase() ?? "") >= 0 ||
            (item.patientBirthDate ?? "").toLowerCase().indexOf(formData.filterPatient?.toLowerCase() ?? "") >= 0);
    }

    function onFilterRowClick(rowData: IRowSelection,  _rowType: TableRowType, _evt: MouseEvent) : void {
        const filterRow = <ICheckInOutDashboardListItem> rowData;
        formData.currentSelectedRow = {
            "appointmentId": filterRow.appointmentId,
            "patientId": filterRow.patientId,
            "patientEid": filterRow.patientEid,
            "patientName": `${filterRow.patientLastName}, ${filterRow.patientFirstName}`,
            "appointmentDate": `${filterRow.appointmentDate} at ${filterRow.startTime}`,
            "resourceId": filterRow.resourceId,
            "resourceName": filterRow.scheduledEntityName,
            "serviceLocationName": filterRow.serviceLocationName,
            "appointmentStatusId": filterRow.appointmentStatusId,
            "commentGroupId": filterRow.commentGroupId
        }
        formData.currentSelectedRowTypeGlobalSearch = false;
        setWorkflowStatus();

        // Add a patient touch
        const patientTouch = { 
            Id: filterRow.patientId,
            EntityId: filterRow.patientEid,
            Name: filterRow.patientName, 
            Birthdate: `${filterRow.patientBirthDate}${filterRow.patientBirthDate ? "(" + filterRow.ageText + ")" : "" }`,
            Gender: filterRow.gender ?? ""
        };
        ChoiceApi.patient.addPatientTouch(patientTouch);
	
        emit("onChange",formData.currentSelectedRow);
    }

    function onSearchRowClick(rowData: IRowSelection,  _rowType: TableRowType, _evt: MouseEvent) : void {
        const searchRow = <ISearchPatientItem> rowData;
        formData.currentSelectedRow = {
            "appointmentId": searchRow.appointment_id,
            "patientId": searchRow.patient_id,
            "patientEid": searchRow.entity_id,
            "patientName": searchRow.patient_name,
            "appointmentDate": searchRow.next_appointment_display ?? "",
            "resourceId": searchRow.resource_id,
            "resourceName": searchRow.resource_name,
            "serviceLocationName": searchRow.service_location_name,
            "appointmentStatusId": searchRow.appointment_status_id,
            "commentGroupId": searchRow.comment_group_id
        }
        formData.currentSelectedRowTypeGlobalSearch = true;
        setWorkflowStatus();

        // Add a patient touch
        const patientTouch = { 
            Id: searchRow.patient_id,
            EntityId: searchRow.entity_id,
            Name: searchRow.patient_name, 
            Birthdate: `${searchRow.birth_date}${searchRow.birth_date ? "(" + searchRow.age + ")" : "" }`,
            Gender: searchRow.gender 
        };
        ChoiceApi.patient.addPatientTouch(patientTouch);

        emit("onChange",formData.currentSelectedRow);
    }

    function setWorkflowStatus() {
        let workflowIndex = -1;
        if (formData.currentSelectedRow?.appointmentId) {
            const statusIndex = formData.statusList.findIndex(o => o.fromStatusId == formData.currentSelectedRow?.appointmentStatusId);
            if (statusIndex >= 0) {
                const newStatusIndex = formData.statusList.findIndex(o => o.fromStatusId == formData.statusList[statusIndex].toStatusId);
                if (newStatusIndex >= 0)
                    workflowIndex = newStatusIndex;
            }
        }

        // Did not find a workflow status that has been setup so will try to default to check-in/check-out
        if (workflowIndex == -1) {
            if (props.tabId == CheckInOutDashboardTab.CheckIn) {
                const checkInIndex = formData.statusList.findIndex(o => o.fromStatusId == AppointmentStatus.CheckedIn)
                if (checkInIndex >= 0) {
                    workflowIndex = checkInIndex;
                }
            }
            else if (props.tabId == CheckInOutDashboardTab.CheckOut) {
                const checkOutIndex = formData.statusList.findIndex(o => o.fromStatusId == AppointmentStatus.CheckedOut)
                if (checkOutIndex >= 0) {
                    workflowIndex = checkOutIndex;
                }
            }
        }

        if (workflowIndex == -1)
            return; 
        toolbarConfig.toolbarList[0].identifier = formData.statusList[workflowIndex].fromStatusId;
        toolbarConfig.toolbarList[0].label = formData.statusList[workflowIndex].fromStatusDescription;
    }

    async function toolbarClick(data: any): Promise<void> {
        if (!formData?.currentSelectedRow?.appointmentId) { 
            popAlert("Please select a row with an appointment.", "Update Status", ModalStyles.info);
            return;
        } 

        formData.newAppointmentStatusId = data;
        if (data == AppointmentStatus.Cancelled) {
            formData.showCancelComment = true;
            return;
        }

        if (data == formData.rescheduledAppointmentStatusId && props.rescheduleCopy) {
            formData.showCancelComment = true;
            return;
        }

        changeStatus(data);
    }
    async function changeStatus(statusId: number) {
        if (!formData.currentSelectedRow)
            return;
        if (!formData?.currentSelectedRow?.appointmentId || !formData?.currentSelectedRow?.patientId)
            return;

        formData.toolbarExecute = true;
        const post_data = {
            patientId: formData.currentSelectedRow.patientId,
            appointmentId: formData.currentSelectedRow.appointmentId,
            appointmentStatusId: statusId
        }

        await nextTick();
        const result = await ChoiceApi.schedule.DashboardUpdateStatus(post_data);
        formData.toolbarExecute = false;
        if (!result.passed) {
            popAlert(result.errorMessage ?? "Failed to Update Status", "Update Status", ModalStyles.warning);
            return;
        }

        // Update the status
        const scheduledItem = formData.statusList.find(o => o.fromStatusId == statusId);
        if (scheduledItem) {
            if (formData.globalPatientSearch) {
                if (formData.currentSelectedRow.appointmentId) {
                    const patientItem = formData.searchTableData.find(item => item.appointment_id == formData?.currentSelectedRow?.appointmentId);
                    if (patientItem) {
                        patientItem.appointment_status_id = statusId;
                        patientItem.appointment_status_desc = scheduledItem.fromStatusDescription;
                    }
                }
            } else {
                const appointmentItem = formData.tableData.find(item => item.appointmentId == formData?.currentSelectedRow?.appointmentId);
                if (appointmentItem) {
                    appointmentItem.appointmentStatusId = statusId;
                    appointmentItem.appointmentStatus = scheduledItem.fromStatusDescription;
                    setFilterTableData();
                }
            }
        }

        emit("onStatusChange",formData.currentSelectedRow);

        let message = [];
        if (result.alertMessage)
            message.push(result.alertMessage);
        if (result.patientValidationExists)
            message.push(result.patientValidationMessage);
        if (result.groupMessageExists)
            message.push(result.groupMessage);
        if (message.length > 0)
            popAlert(message.join("<p>"), "Update Status", ModalStyles.info);

        if (props.multipleCheckIn && statusId == AppointmentStatus.CheckedIn) {
            const resultCheckIn = await ChoiceApi.schedule.PatientMultipleCheckInExists(formData.currentSelectedRow.appointmentId);
            if (resultCheckIn?.multipleExists) {
                const result = await ChoiceApi.schedule.PatientMultipleCheckInList(formData.currentSelectedRow.appointmentId);
                if (result) {
                    formData.multipleCheckInResults = result;
                    formData.displayMultipleCheckIn = true;
                }
                return;
            }
        }

    }

    function onTextUpdate() : void {
        setFilterTableData();
    }

    async function onShowFilter() {
        formData.keyFilter += 1;
        formData.showFilter = true;
    }

    async function onHideFilter() {
        formData.showFilter = false;
    }

    function filterChange(selectedItem : any) : void {
        formData.filterId = (selectedItem == undefined ? null : selectedItem.filterId);
        refreshSearch();
    }

    async function changeToFilter() : Promise<void> {
        await refreshFilterList();
        await refreshTabData();
    }

    async function onPatientSearch() {
        const post_data = {
            searchText: formData.searchPatient,
            displayAllResults: false,
            displayNextAppointment: true
        }
        const searchResults = await ChoiceApi.patient.search(post_data);
        formData.searchTableData = searchResults?.searchList ?? []
    }

    async function onShowStatus() {
        formData.keyStatus += 1;
        formData.showStatus = true;
    }

    async function onHideStatus() {
        formData.showStatus = false;
    }

    async function changeToSetup() : Promise<void> {
        await refreshStatusList();
    }

    function CloseMultipleCheckIn() {
        refreshNow();
        formData.displayMultipleCheckIn = false;
    }

    async function onHideCancelComment() {
        formData.showCancelComment = false;
    }    

	function cancelCommentSink(payload: any)
	{
        if (!payload.completed) {
            formData.showCancelComment = false;
            return;
        }

        if (formData.currentSelectedRow?.appointmentId == payload.appointmentId) {
            if (formData.newAppointmentStatusId)
                changeStatus(formData.newAppointmentStatusId);
            formData.showCancelComment = false;

            if (formData.newAppointmentStatusId == formData.rescheduledAppointmentStatusId && formData.rescheduledAppointmentStatusId) {
                MessageLib.MainWindow.openSchedule();
                MessageLib.Scheduler.loadRescheduled(
                    {patientEid: formData.currentSelectedRow?.patientEid, 
                    resourceId: formData.currentSelectedRow?.resourceId, 
                    scheduleDate: formData.currentSelectedRow?.appointmentDate.substring(0,10),
                    appointmentId: formData.currentSelectedRow?.appointmentId }
                ); 
                if (window.top.location.href.toLowerCase().includes("checkinoutdashboard"))
                    popAlert("Please navigate to the scheduler to reschedule a new appointment time.", "Reschedule Appointment", ModalStyles.info);
            }
        }
    }

    async function onGlobalPatientSearch() {
        if (formData.globalPatientSearch) {
            if ((formData.filterPatient ?? "").length == 0)
                return;
            formData.searchPatient = formData.filterPatient;
            if ((formData.searchPatient ?? "").length >= 3) {
                await globalSearchButton.value?.actionButtonClick();
            }
        } else {
            formData.filterPatient = undefined;
            setFilterTableData();
        }
    }
    
</script>

<template>
    <ResizeMonitor @resize="windowResize" />
    <div class="d-flex flex-row mt-3">
        <div class="col-4 isalus-section-header label-isalus-small">
            Schedule
        </div>
        <div class="col ps-2 isalus-section-header label-isalus-small">
            Filter
        </div>
    </div>    
    <div class="d-flex flex-row mt-1">
        <div class="col-4 pe-2">
            <DatePickerNoTZ
                v-model:value="formData.scheduleDate" 
                :clearable="false" 
                :required=true
                :errorDisplay=false 
                :placeholder="'Schedule Date'"
                :cssFramework="'vanilla'"
                @update:value="()=>refreshSearch()"
                /> 
        </div>
        <div class="col ps-2">
            <Combo 
                :optionList="formData.filterList"
                optionValue="filterId"
                :includeBlank="false"
                v-model:selected="formData.filterId"
                @onChange="filterChange" />
        </div>
        <div class="d-flex ps-2 justify-conent-end">
            <Toolbar 
                :configuration="settingConfig" 
                :toolbarStyle="ToolbarStyle.outline" 
                :toolbarPadding="ToolbarPadding.paddingOnlyLeftRight"
                :toolbarBackground="ToolbarBackground.white"/>              
        </div>
    </div>
    <div class="row mt-2 mb-0">
        <div v-if="!formData.globalPatientSearch" class="col isalus-section-header label-isalus-small">
            Filter {{ tabTitle }} List
        </div>
        <div v-if="formData.globalPatientSearch" class="col isalus-section-header label-isalus-small">
            Search Patient
        </div>    
    </div>
    <div class="d-flex flex-row mt-0">
        <div class="col-4 pe-2 mt-2">
            <TextInput 
                v-if="!formData.globalPatientSearch"
                v-model:textValue="formData.filterPatient" 
                :cssFramework="'vanilla'"  
                placeholder="Filter by chart#, name"
                @update:text-value="onTextUpdate()">
                <template #RightAddon>
                    <i class="fa-solid fa-filter"></i>
                </template>
            </TextInput>
            <div v-show="formData.globalPatientSearch" class="row">
                <div class="col pe-1">
                    <TextInput 
                        v-model:textValue="formData.searchPatient" 
                        :cssFramework="'vanilla'"  
                        placeholder="Search by chart#, name"
                        @keydown.enter="onPatientSearch">
                    </TextInput>
                </div>
                <div class="col-auto ps-0">
                    <ActionButtonV2 
                        ref="globalSearchButton"
                        :action="ActionButtonStyle.Search"
                        :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                        text=""
                        :size="ButtonDisplaySize.Small" 
                        tooltip="Search for patient using the search criteria."
                        :performAction="onPatientSearch" /> 
                </div>
            </div>
        </div>
        <div class="col ps-2 mt-2">
            <Checkbox v-model="formData.globalPatientSearch" :title="'Search for patient across all schedules.'" @update:modelValue="onGlobalPatientSearch()"/>  
            <span label class="ps-1 pe-3 label-isalus-small">Global Search</span>                    
        </div>
        <div class="d-flex justify-conent-end mt-0">
            <Toolbar 
                :configuration="toolbarConfig" 
                :toolbarSize="ToolbarSize.regular" 
                :toolbarStyle="ToolbarStyle.outline" 
                :toolbarBackground="ToolbarBackground.white"
                :spin="formData.toolbarExecute"
                :padding="ToolbarPadding.paddingOnlyLeftRight"
                @toolbarClick="toolbarClick"/>
        </div>
    </div>
    <div class="row mt-2">
        <div class="col">
            <ChoiceTable 
                v-if="!formData.globalPatientSearch"
                ref="filterTable"
                :columns="filterColumns" 
                :data="formData.filterTableData" 
                :show-filters="false" 
                :allowSorting="false"
                :height="tableHeight"
                fontSize="smaller"
                :rowHighlight="TableRowHighlight.onSingleClick"
                @rowclick="onFilterRowClick"
                :key-value-data-member="'appointmentId'">
            </ChoiceTable>
            <ChoiceTable 
                v-if="formData.globalPatientSearch"
                ref="searchTable"
                :columns="searchColumns" 
                :data="formData.searchTableData" 
                :show-filters="false" 
                :allowSorting="false"
                :height="tableHeight"
                fontSize="smaller"
                :rowHighlight="TableRowHighlight.onSingleClick"
                @rowclick="onSearchRowClick"
                :key-value-data-member="'patient_id'">
            </ChoiceTable>
        </div>
    </div>
    <div class="row mt-2" v-if="maxRowsReturned && formData.globalPatientSearch">
        <div class="col red-text label-isalus-small">
            *** Note: Only the top 50 search results are displayed.
        </div>
    </div>
    <ModalDialog :show="formData.showFilter" :size="ModalSize.Large" @hide="onHideFilter">
        <template #title>{{ tabTitle }} Filter Settings</template>
        <CheckInOutFilter
            :key="formData.keyFilter"
            :tabId="props.tabId"
            @changeToFilter="changeToFilter">
        </CheckInOutFilter>
    </ModalDialog>
    <ModalDialog :show="formData.showStatus" :size="ModalSize.Large" @hide="onHideStatus">
        <template #title>{{ tabTitle }} Automated Workflow Settings</template>
        <CheckInOutStatusSetup
            :key="formData.keyStatus"
            :tabId="props.tabId"
            @changeToSetup="changeToSetup">
        </CheckInOutStatusSetup>
    </ModalDialog>
    <template v-if="formData.displayMultipleCheckIn">
        <PatientMultipleCheckIn 
            :appointmentId="formData.currentSelectedRow?.appointmentId ?? 0"
            :appointmentResults="formData.multipleCheckInResults"
            :saveEnabled="1"
            :pageTitle="multipleCheckInTitle"
            @close-vue-modal-iframe="CloseMultipleCheckIn()"/>
    </template>
    <template v-if="formData.showCancelComment">
        <ModalDialog :show="formData.showCancelComment" :size="ModalSize.Large" :closeOnClickOutside="false" @hide="onHideCancelComment">
            <template #title>Cancel Comments - {{ formData.currentSelectedRow?.patientName }}</template>
            <CancelCommentLegacy
                :appointmentId="formData.currentSelectedRow?.appointmentId ?? 0" />
        </ModalDialog>
    </template>
</template>

